/* eslint-disable complexity */
import { useRef } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import type { TFields } from '../../models/analytics';

import type {
    TMappedFields,
    TMappedFioFields,
    TFioFields,
    IOnBlurFieldAnalyticsArgs,
} from './models';

const fioFieldKeys: TFioFields[] = ['firstName', 'lastName', 'middleName'];

const checkFieldState =
    (prevState: TMappedFields[TFields]) => (currentState: TMappedFields[TFields]) =>
        prevState?.error !== currentState?.error || prevState?.value !== currentState?.value;

export function useCachedValue<T>(initValue: T): [T, (newValue: T) => void] {
    const value = useRef<T>(initValue);

    return [
        value.current,
        newValue => {
            value.current = newValue;
        },
    ];
}

export function isValidFioFields(fields: TMappedFioFields) {
    return fioFieldKeys.every(
        fieldName =>
            !fields[fieldName]?.error && fields[fieldName]?.value && fields[fieldName]?.touched
    );
}

export const onBlurFieldAnalytics = ({
    active,
    activeField,
    analytics,
    cachedActive,
    cachedActiveField,
    fields,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
IOnBlurFieldAnalyticsArgs): void => {
    /**
     * новые значения для поля, с которого перевели фокус
     * */
    const { error, touched } = activeField || {};
    /**
     * сравнение стейтов поля для определения дальнейших действий
     * */
    const isFieldChanged = checkFieldState(cachedActiveField)(activeField);

    const isOutOfField = touched && cachedActive && active === undefined && cachedActive !== active;

    if (touched && cachedActive !== active && cachedActive !== 'fullName') {
        if (error && active === undefined) {
            return sendEvent({ ...analytics[cachedActive]?.incorrect, error });
        }
        if (!error && (isFieldChanged || cachedActive === 'email')) {
            return sendEvent(analytics[cachedActive]?.correct);
        }
    }
    /**
     * КОСТЫЛЬ для ФИО, т.к. при изменении `fullName` меняется еще 3 поля (firstName, middleName, lastName)
     * Поэтому отдельно обрабатываем ФИО
     * */
    if (touched && !error && cachedActive === 'fullName' && isOutOfField) {
        const isFioFieldsHaveErrors = fioFieldKeys.some(key => fields[key]?.error);

        if (isFioFieldsHaveErrors) return;

        return sendEvent(analytics[cachedActive]?.correct);
    }
    // Обрабатываем состояние ошибки ФИО
    if (touched && error && cachedActive === 'fullName' && isOutOfField) {
        return sendEvent({ ...analytics[cachedActive]?.incorrect, error });
    }
};
